import type { ConfirmPayment } from '@admin/features/confirm-payments/libs/type';
import type { ErrorResponse } from '@shared/types/http';

import useAuth from '@frontend/hooks/useAuth';
import { ImageSize } from '@frontend/libs/types/media';
import { Button, Image } from '@nextui-org/react';
import { ControlledRadioGroup, ControlledTextInput, TextEditorInput } from '@shared/components/controlled-fields';
import { INPUT_VARIANT } from '@shared/constant';
import { endpoint } from '@shared/services/endpoint';
import { handleResponse } from '@shared/services/fetcher';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import type { BankAccount } from './libs/type';

import useConfirmPayment from './hooks/useConfirmPayment';

export type ConfirmPaymentFormState = {
    slip: FileList | null;
    transferAmount: string;
} & Omit<ConfirmPayment, 'createdAt' | 'id' | 'slip' | 'status' | 'transferAmount' | 'transferDate' | 'updatedAt'>;

const defaultValues: ConfirmPaymentFormState = {
    bank: '',
    name: '',
    order: '',
    phoneNumber: '',
    slip: null,
    transferAmount: '',
};

export interface ConfirmPaymentFormProps {
    orderId?: number;
}

export function ConfirmPaymentForm({ orderId }: ConfirmPaymentFormProps) {
    const {
        control,
        formState: { errors },
        handleSubmit,
        register,
        setValue,
    } = useForm<ConfirmPaymentFormState>({
        defaultValues,
    });

    useEffect(() => {
        if (orderId) {
            setValue('order', orderId.toString());
        }
    }, [orderId, setValue]);

    const { isLoggedIn, user } = useAuth();

    useEffect(() => {
        if (isLoggedIn) {
            setValue('name', user?.firstName + ' ' + user?.lastName);
            setValue('phoneNumber', user?.phoneNumber || '');
        }
    }, [isLoggedIn, user, setValue]);

    const [isLoading, setIsLoading] = useState(false);
    const { data, error } = useConfirmPayment().getBankAccount();

    if (error) {
        toast.error(error?.message || 'Failed to fetch bank accounts');
    }

    const handleOnSubmit: SubmitHandler<ConfirmPaymentFormState> = async (data) => {
        try {
            setIsLoading(true);

            const formData = new FormData();

            formData.append('name', data.name);
            formData.append('phoneNumber', data.phoneNumber);
            formData.append('order', data.order.toString());
            formData.append('transferAmount', data.transferAmount);
            formData.append('bank', data.bank);
            formData.append('slip', data.slip?.[0] as File);

            const response = await fetch(endpoint.frontend.CONFIRM_PAYMENT.INDEX, {
                body: formData,
                headers: {
                    Accept: 'application/json',
                    ContentType: 'multipart/form-data',
                },
                method: 'POST',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData?.error?.message || errorData?.message || 'Failed to submit confirm payment');
            }

            const result = await response.json();
            console.log(result);
            toast.success(result?.message || 'Confirm payment submitted');
        } catch (error: Error | ErrorResponse | unknown) {
            console.log(error);
            toast.error(error?.message || 'Failed to submit confirm payment');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form noValidate onSubmit={handleSubmit(handleOnSubmit)}>
            <div className="grid grid-cols-2 gap-6">
                <ControlledTextInput
                    control={control}
                    fieldProps={{
                        label: 'Name',
                        variant: INPUT_VARIANT,
                    }}
                    name="name"
                    rules={{
                        required: 'Name is required',
                    }}
                />
                <ControlledTextInput
                    control={control}
                    fieldProps={{
                        inputMode: 'tel',
                        label: 'Phone number',
                        variant: INPUT_VARIANT,
                    }}
                    name="phoneNumber"
                    rules={{
                        required: 'Phone number is required',
                    }}
                />
                <ControlledTextInput
                    control={control}
                    fieldProps={{
                        inputMode: 'numeric',
                        label: 'Order number',
                        type: 'number',
                        variant: INPUT_VARIANT,
                    }}
                    name="order"
                    rules={{
                        pattern: {
                            message: 'Order number must be a number',
                            value: /^[0-9]*$/,
                        },
                        required: 'Order is required',
                    }}
                />
                <ControlledTextInput
                    control={control}
                    fieldProps={{
                        inputMode: 'numeric',
                        label: 'Transfer Amount',
                        variant: INPUT_VARIANT,
                    }}
                    name="transferAmount"
                    rules={{
                        required: 'Transfer Amount is required',
                    }}
                />
                <ControlledRadioGroup
                    control={control}
                    fieldProps={{
                        classNames: {
                            base: 'col-span-2',
                            label: 'text-primary uppercase',
                        },
                        label: 'Bank transfer',
                    }}
                    name="bank"
                    options={
                        data?.map((item) => ({
                            label: <BankDetail {...item} />,
                            value: item.bankName,
                        })) || []
                    }
                    rules={{
                        required: 'Bank transfer is required',
                    }}
                />
                <div className="col-span-2">
                    <label className="mb-2 block uppercase" htmlFor="slip">
                        Transfer Slip
                    </label>
                    <div className="border p-4">
                        <input
                            id="slip"
                            type="file"
                            {...register('slip', {
                                required: 'Transfer slip is required',
                                validate: (value) => {
                                    const file = value?.[0];

                                    if (!file) {
                                        return 'Transfer slip is required';
                                    }

                                    const acceptedFiles = ['image/jpeg', 'image/png', 'image/jpg'];

                                    if (!acceptedFiles.includes(file.type)) {
                                        return 'Only accept jpeg, png, jpg';
                                    }

                                    if (file.size > 2 * 1024 * 1024) {
                                        return 'File size must be less than 2MB';
                                    }

                                    return true;
                                },
                            })}
                            accept="image/jpeg, image/png, image/jpg"
                        />
                        <span className="mt-2 block text-xs text-default-600">
                            Only accept jpeg, png, jpg. File size must be less than 2MB
                        </span>
                    </div>
                    {errors.slip && <span className="mt-2 block text-xs text-danger">{errors.slip.message}</span>}
                </div>
                <div className="flex gap-2">
                    <Button color="primary" isLoading={isLoading} type="submit" variant="solid">
                        Submit
                    </Button>
                </div>
            </div>
        </form>
    );
}

export default ConfirmPaymentForm;

function BankDetail({ accountName, accountNumber, bankLogo, bankName }: BankAccount) {
    return (
        <div className="flex items-center gap-6">
            <span>
                <Image height={40} src={bankLogo.sizes[ImageSize.Medium]} width={40} />
            </span>
            <span>{bankName}</span>
            <span>{accountNumber}</span>
            <span>{accountName}</span>
        </div>
    );
}
